import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fireCelebration } from "@/lib/celebrate";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  US_STATES, YEARS_TEACHING, YEARS_IN_BUSINESS, STUDENTS_PER_WEEK,
  EMPLOYEES, BUSINESS_MODEL, HOW_HEARD, TSHIRT_SIZES,
} from "@/lib/formOptions";
import { TICKETS } from "@/components/TicketsBlock";
import { CheckCircle2, Users, BedDouble, Ticket, ArrowRight } from "lucide-react";

const initial = {
  first_name: "", last_name: "", email: "", phone: "", city: "", state: "",
  country: "United States", business_name: "", years_teaching: "",
  years_in_business: "", students_per_week: "", employees: "",
  business_model: "", how_heard: "", tshirt_size: "",
  food_allergies: "No", food_allergies_details: "",
  physical_limitations: "No", physical_limitations_details: "",
  learning_goals: "",
  stay_on_property: "Not sure",
  panel_apply: "No",
  panel_why_fit: "",
  panel_benefit_you: "",
  panel_benefit_group: "",
};

const Field = ({ label, required, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="text-sm font-semibold text-[#0A1128]">
      {label} {required && <span className="text-[#0066FF]">*</span>}
    </Label>
    {children}
  </div>
);

const SectionTitle = ({ kicker, title }) => (
  <div className="mb-6">
    <div className="text-xs font-bold tracking-[0.2em] text-[#0066FF] uppercase mb-2">{kicker}</div>
    <h3 className="font-display-tight text-2xl text-[#0A1128]">{title}</h3>
  </div>
);

export default function InterestForm({ defaultPanelApply = false, mode = "interest", initialTier = "" }) {
  const isRegister = mode === "register";
  const isPaid = mode === "paid";
  const [data, setData] = useState(() => {
    const wantsPanel =
      defaultPanelApply ||
      (typeof window !== "undefined" && window.location.hash.includes("panel=1"));
    return wantsPanel ? { ...initial, panel_apply: "Yes" } : initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [selectedTier, setSelectedTier] = useState(initialTier || "");
  const [singleDayChoice, setSingleDayChoice] = useState("");
  const [stripeHref, setStripeHref] = useState("");

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // When parent flips defaultPanelApply -> true after mount, flip the form to Yes too.
  useEffect(() => {
    if (defaultPanelApply) {
      setData((d) => (d.panel_apply === "Yes" ? d : { ...d, panel_apply: "Yes" }));
    }
  }, [defaultPanelApply]);

  useEffect(() => {
    if (initialTier) setSelectedTier(initialTier);
  }, [initialTier]);

  const submit = async (e) => {
    e.preventDefault();
    const required = [
      "first_name","last_name","email","state","country","business_name",
      "years_teaching","years_in_business","students_per_week","employees",
      "business_model","tshirt_size","food_allergies",
    ];
    for (const f of required) {
      if (!data[f]) {
        toast.error(`Please fill in: ${f.replace(/_/g, " ")}`);
        return;
      }
    }
    if (data.panel_apply === "Yes" && !data.panel_why_fit.trim()) {
      // Panel applications are closed — force No before submission.
      data.panel_apply = "No";
    }

    let tier = null;
    if (isRegister) {
      tier = TICKETS.find((t) => t.slug === selectedTier);
      if (!tier) {
        toast.error("Please pick a ticket to continue");
        return;
      }
      if (tier.soldOut) {
        toast.error("That tier is sold out — please pick another");
        return;
      }
      if (tier.requiresDayChoice && !singleDayChoice) {
        toast.error("Please pick Friday or Saturday for your Day Pass");
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = { ...data };
      if (tier) {
        payload.selected_tier = tier.slug;
        payload.selected_tier_label = tier.requiresDayChoice
          ? `${tier.name} · ${singleDayChoice}`
          : tier.name;
        if (tier.requiresDayChoice) {
          payload.single_day_choice = singleDayChoice;
        }
      }
      if (isPaid) {
        payload.payment_method = "external";  // e.g. Zelle
        payload.selected_tier = "external-paid";
        payload.selected_tier_label = "Paid externally";
      }
      await api.post("/interest", payload);
      fireCelebration();
      if (tier) setStripeHref(tier.href);
      setDone(true);
      toast.success(
        isPaid
          ? "You're registered! Redirecting to WhatsApp…"
          : isRegister
          ? "Registration saved — off to Stripe!"
          : "You're on the list!"
      );
      if (isPaid) {
        // Redirect to WhatsApp group after a short beat so the success screen registers
        setTimeout(() => {
          window.location.href = "https://chat.whatsapp.com/HSRkuBWMRztKlJYytthG6u";
        }, 2500);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    if (isPaid) {
      return (
        <div data-testid="paid-form-success" className="bg-white border-2 border-[#25D366] rounded-xl p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-[#25D366]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#FACC15]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-[#25D366] flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle2 size={40} className="text-white" strokeWidth={2.5} />
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#0A1128] text-[#FACC15] rounded-full mb-6 text-[10px] font-black uppercase tracking-[0.25em]">
              ★ Registration Complete
            </div>
            <h3 className="font-display text-4xl md:text-5xl text-[#0A1128] mb-4">You&apos;re in! 🎉</h3>
            <p className="text-[#4B5563] max-w-md mx-auto text-lg mb-4">
              Your registration is locked in. Since your ticket was paid separately, all that&apos;s left is joining the group.
            </p>
            <p className="text-[#6B7280] max-w-md mx-auto text-sm mb-8">
              You&apos;ll be automatically redirected to WhatsApp in a moment. If it doesn&apos;t open, tap the button below.
            </p>
            <a
              href="https://chat.whatsapp.com/HSRkuBWMRztKlJYytthG6u"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="paid-whatsapp-join"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FAF53] text-white font-black uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
            >
              Join the WhatsApp Group →
            </a>
          </div>
        </div>
      );
    }
    if (isRegister && stripeHref) {
      return (
        <div data-testid="register-form-success" className="bg-white border-2 border-[#FACC15] rounded-xl p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-[#FACC15]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#0066FF]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-[#FACC15] flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle2 size={40} className="text-[#0A1128]" strokeWidth={2.5} />
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#0A1128] text-[#FACC15] rounded-full mb-6 text-[10px] font-black uppercase tracking-[0.25em]">
              ★ Info Saved · One Step to Go
            </div>
            <h3 className="font-display text-4xl md:text-5xl text-[#0A1128] mb-4">You&apos;re almost in! 🎉</h3>
            <p className="text-[#4B5563] max-w-md mx-auto text-lg mb-4">
              Your registration info is safely stored on our end. Head to Stripe to complete payment and lock in your spot.
            </p>
            <p className="text-[#6B7280] max-w-md mx-auto text-sm mb-8">
              A confirmation email is also on its way to your inbox.
            </p>
            <a
              href={stripeHref}
              data-testid="register-continue-to-stripe"
              className="inline-flex items-center justify-center gap-2 bg-[#0A1128] hover:bg-[#0066FF] text-white font-black uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors mb-8"
            >
              Continue to Stripe → Complete Payment
              <ArrowRight size={16} />
            </a>
            <div className="bg-[#0A1128] rounded-2xl p-6 max-w-md mx-auto text-left">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                    <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm0 18.15a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.23 8.23 0 0 1-1.26-4.39c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.11-.23-.17-.48-.29z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#25D366] mb-1">While you&apos;re here</div>
                  <div className="font-display-tight text-lg text-white leading-tight mb-2">Join the SSU Workshops group chat</div>
                  <p className="text-sm text-white/70 mb-4 leading-snug">
                    Live updates, behind-the-scenes, and pre-event hype — straight from Jacob and the SSU community.
                  </p>
                  <a
                    href="https://chat.whatsapp.com/HSRkuBWMRztKlJYytthG6u"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="register-whatsapp-join"
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FAF53] text-white font-black uppercase tracking-wider text-xs px-5 py-3 rounded-md transition-colors"
                  >
                    Join the WhatsApp Group →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div data-testid="interest-form-success" className="bg-white border-2 border-[#FACC15] rounded-xl p-10 md:p-14 text-center relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-[#FACC15]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#0066FF]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-[#FACC15] flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle2 size={40} className="text-[#0A1128]" strokeWidth={2.5} />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#0A1128] text-[#FACC15] rounded-full mb-6 text-[10px] font-black uppercase tracking-[0.25em]">
            ★ You&apos;re In · 30 Seats Only
          </div>
          <h3 className="font-display text-4xl md:text-5xl text-[#0A1128] mb-4">You&apos;re on the list! 🎉</h3>
          <p className="text-[#4B5563] max-w-md mx-auto text-lg mb-8">
            We&apos;ll be in touch as soon as tickets open. Keep an eye on your inbox — and check your spam just in case.
          </p>
          <div className="bg-[#0A1128] rounded-2xl p-6 max-w-md mx-auto text-left">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                  <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm0 18.15a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.23 8.23 0 0 1-1.26-4.39c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.11-.23-.17-.48-.29z"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-black uppercase tracking-widest text-[#25D366] mb-1">Next step</div>
                <div className="font-display-tight text-lg text-white leading-tight mb-2">Join the SSU Workshops group chat</div>
                <p className="text-sm text-white/70 mb-4 leading-snug">
                  Live updates, behind-the-scenes, and pre-event hype — straight from Jacob and the SSU community.
                </p>
                <a
                  href="https://chat.whatsapp.com/HSRkuBWMRztKlJYytthG6u"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="interest-whatsapp-join"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FAF53] text-white font-black uppercase tracking-wider text-xs px-5 py-3 rounded-md transition-colors"
                >
                  Join the WhatsApp Group →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inputCls = "h-11 border-[#E5E7EB] focus-visible:ring-[#0066FF] focus-visible:ring-2 bg-white";

  return (
    <form onSubmit={submit} data-testid="interest-form" className="bg-white border border-[#E5E7EB] rounded-xl p-6 md:p-10 space-y-10">
      <section>
        <SectionTitle kicker="01" title="Personal Information" />
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="First Name" required testid="field-first-name">
            <Input className={inputCls} data-testid="input-first-name" value={data.first_name} onChange={(e) => set("first_name", e.target.value)} />
          </Field>
          <Field label="Last Name" required testid="field-last-name">
            <Input className={inputCls} data-testid="input-last-name" value={data.last_name} onChange={(e) => set("last_name", e.target.value)} />
          </Field>
          <Field label="Email Address" required testid="field-email">
            <Input type="email" className={inputCls} data-testid="input-email" value={data.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Phone Number" testid="field-phone">
            <Input className={inputCls} data-testid="input-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
          <Field label="City" testid="field-city">
            <Input className={inputCls} data-testid="input-city" value={data.city} onChange={(e) => set("city", e.target.value)} />
          </Field>
          <Field label="State" required testid="field-state">
            <Select value={data.state} onValueChange={(v) => set("state", v)}>
              <SelectTrigger className={inputCls} data-testid="select-state"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Country" required testid="field-country">
            <Input className={inputCls} data-testid="input-country" value={data.country} onChange={(e) => set("country", e.target.value)} />
          </Field>
        </div>
      </section>

      <section>
        <SectionTitle kicker="02" title="Your Swim Business" />
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Swim School / Business Name" required testid="field-business-name">
            <Input className={inputCls} data-testid="input-business-name" value={data.business_name} onChange={(e) => set("business_name", e.target.value)} />
          </Field>
          <Field label="How long have you been teaching swim lessons?" required testid="field-years-teaching">
            <Select value={data.years_teaching} onValueChange={(v) => set("years_teaching", v)}>
              <SelectTrigger className={inputCls} data-testid="select-years-teaching"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{YEARS_TEACHING.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="How long operating as a business?" required testid="field-years-business">
            <Select value={data.years_in_business} onValueChange={(v) => set("years_in_business", v)}>
              <SelectTrigger className={inputCls} data-testid="select-years-business"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{YEARS_IN_BUSINESS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Students per week" required testid="field-students">
            <Select value={data.students_per_week} onValueChange={(v) => set("students_per_week", v)}>
              <SelectTrigger className={inputCls} data-testid="select-students"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{STUDENTS_PER_WEEK.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Employees / contractors" required testid="field-employees">
            <Select value={data.employees} onValueChange={(v) => set("employees", v)}>
              <SelectTrigger className={inputCls} data-testid="select-employees"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{EMPLOYEES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Business model" required testid="field-business-model">
            <Select value={data.business_model} onValueChange={(v) => set("business_model", v)}>
              <SelectTrigger className={inputCls} data-testid="select-business-model"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{BUSINESS_MODEL.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="How did you hear about SSU Live?" testid="field-how-heard">
            <Select value={data.how_heard} onValueChange={(v) => set("how_heard", v)}>
              <SelectTrigger className={inputCls} data-testid="select-how-heard"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{HOW_HEARD.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      <section>
        <SectionTitle kicker="03" title="Workshop Preferences & Logistics" />
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="T-Shirt Size" required testid="field-tshirt">
            <Select value={data.tshirt_size} onValueChange={(v) => set("tshirt_size", v)}>
              <SelectTrigger className={inputCls} data-testid="select-tshirt"><SelectValue placeholder="Select size" /></SelectTrigger>
              <SelectContent>{TSHIRT_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Food allergies / dietary restrictions?" required testid="field-allergies">
            <Select value={data.food_allergies} onValueChange={(v) => set("food_allergies", v)}>
              <SelectTrigger className={inputCls} data-testid="select-allergies"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="No">No</SelectItem><SelectItem value="Yes">Yes</SelectItem></SelectContent>
            </Select>
          </Field>
          {data.food_allergies === "Yes" && (
            <Field label="Please describe" testid="field-allergies-details">
              <Input className={inputCls} data-testid="input-allergies-details" value={data.food_allergies_details} onChange={(e) => set("food_allergies_details", e.target.value)} />
            </Field>
          )}
          <Field label="Physical disabilities or mobility limitations?" testid="field-physical">
            <Select value={data.physical_limitations} onValueChange={(v) => set("physical_limitations", v)}>
              <SelectTrigger className={inputCls} data-testid="select-physical"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="No">No</SelectItem><SelectItem value="Yes">Yes</SelectItem></SelectContent>
            </Select>
          </Field>
          {data.physical_limitations === "Yes" && (
            <Field label="Please tell us more so we can accommodate you" testid="field-physical-details">
              <Input className={inputCls} data-testid="input-physical-details" value={data.physical_limitations_details} onChange={(e) => set("physical_limitations_details", e.target.value)} />
            </Field>
          )}
          <div className="md:col-span-2">
            <Field label="What are you hoping to learn or get out of this workshop?" testid="field-learning">
              <Textarea data-testid="input-learning" rows={4} className="border-[#E5E7EB] focus-visible:ring-[#0066FF] focus-visible:ring-2 bg-white" value={data.learning_goals} onChange={(e) => set("learning_goals", e.target.value)} />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Are you interested in staying on the property?" required testid="field-stay-property">
              <div className="rounded-lg border border-[#E5E7EB] bg-[#FAFAF7] p-4 mb-3 text-sm text-[#4B5563] leading-relaxed flex items-start gap-3">
                <BedDouble size={18} className="text-[#0066FF] mt-0.5 shrink-0" />
                <div>
                  <span className="font-bold text-[#0A1128]">There are a limited number of beds at the estate.</span> We&apos;re not yet selling lodging — we&apos;re just gauging interest so we know how many on-property rooms to offer. Off-property hotels and Airbnbs are easy to find nearby either way.
                </div>
              </div>
              <Select value={data.stay_on_property} onValueChange={(v) => set("stay_on_property", v)}>
                <SelectTrigger className={inputCls} data-testid="select-stay-property"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes — I&apos;d love to stay on the property</SelectItem>
                  <SelectItem value="No">No — I&apos;ll book my own lodging</SelectItem>
                  <SelectItem value="Not sure">Not sure yet</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>
      </section>

      {/* PANEL APPLICATION — CLOSED (panel is full, 6/6 confirmed) */}
      <section id="panel-application">
        <div
          data-testid="panel-application-closed"
          className="rounded-2xl bg-gradient-to-br from-[#FACC15]/10 via-white to-[#0066FF]/10 border-2 border-dashed border-[#FACC15]/50 p-6 md:p-8"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-[#FACC15] flex items-center justify-center">
              <Users size={22} className="text-[#0A1128]" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-black tracking-[0.2em] text-[#A16207] uppercase mb-1">04 · Panel Applications Closed</div>
              <h3 className="font-display-tight text-2xl md:text-3xl text-[#0A1128] leading-tight">
                The panel is full.
              </h3>
              <p className="text-sm text-[#4B5563] mt-2 max-w-xl">
                All six panel seats are confirmed — Megan, Michele, Wendy, Shauna, Laura, and Alex. Applications are closed for this workshop. You can still see the whole lineup on the main page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {isRegister && (
        <section data-testid="section-ticket-picker">
          <SectionTitle kicker="05" title="Pick Your Ticket" />
          <p className="text-sm text-[#4B5563] mb-5 -mt-3">
            Choose the ticket you want to purchase. When you hit submit we&apos;ll save your info and send you straight to Stripe to complete payment.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {TICKETS.map((t) => {
              const isSelected = selectedTier === t.slug;
              const disabled = Boolean(t.soldOut);
              return (
                <button
                  key={t.slug}
                  type="button"
                  disabled={disabled}
                  data-testid={`register-tier-${t.slug}`}
                  onClick={() => !disabled && setSelectedTier(t.slug)}
                  className={`text-left border-2 rounded-xl p-4 transition-all ${
                    disabled
                      ? "border-[#E5E7EB] bg-[#F9FAFB] opacity-60 cursor-not-allowed"
                      : isSelected
                      ? "border-[#0066FF] bg-[#EFF6FF] shadow-md"
                      : "border-[#E5E7EB] bg-white hover:border-[#0066FF]/60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-display-tight text-lg text-[#0A1128] leading-tight">{t.name}</div>
                    {disabled ? (
                      <span className="text-[9px] font-black uppercase tracking-widest bg-red-600 text-white px-2 py-0.5 rounded-full">Sold Out</span>
                    ) : isSelected ? (
                      <CheckCircle2 size={20} className="text-[#0066FF] shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-[#D1D5DB] shrink-0" />
                    )}
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280] mb-2">{t.sub}</div>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-display text-xl ${disabled ? "text-[#9CA3AF] line-through" : "text-[#0A1128]"}`}>{t.price}</span>
                    {t.priceNote && <span className={`text-xs ${disabled ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}>{t.priceNote}</span>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Day Pass — pick your day */}
          {(() => {
            const activeTier = TICKETS.find((t) => t.slug === selectedTier);
            if (!activeTier?.requiresDayChoice) return null;
            const DAYS = [
              { value: "Friday", label: "Friday", sub: "Oct 2 · Business + In-Water · Panel · Group Dinner" },
              { value: "Saturday", label: "Saturday", sub: "Oct 3 · In-Water + Business · Mentor Lunch · Optional Outing" },
            ];
            return (
              <div
                data-testid="single-day-picker"
                className="mt-5 border-2 border-[#22C55E]/40 bg-[#F0FDF4] rounded-xl p-4 md:p-5"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-[#22C55E] text-white flex items-center justify-center">
                    <Ticket size={16} />
                  </div>
                  <div>
                    <div className="text-[10px] font-black tracking-[0.25em] text-[#166534] uppercase mb-0.5">
                      Day Pass · Pick Your Day
                    </div>
                    <div className="font-display-tight text-base md:text-lg text-[#0A1128] leading-tight">
                      Which day are you coming?
                    </div>
                    <p className="text-xs text-[#4B5563] mt-1">
                      You get everything happening that day — presentations, meals, and estate access during workshop hours.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {DAYS.map((d) => {
                    const isSel = singleDayChoice === d.value;
                    return (
                      <button
                        key={d.value}
                        type="button"
                        data-testid={`single-day-${d.value.toLowerCase()}`}
                        onClick={() => setSingleDayChoice(d.value)}
                        className={`text-left border-2 rounded-lg p-3 transition-all ${
                          isSel
                            ? "border-[#22C55E] bg-white shadow-md"
                            : "border-[#D1FAE5] bg-white hover:border-[#22C55E]/60"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="font-display-tight text-base text-[#0A1128]">{d.label}</div>
                          {isSel ? (
                            <CheckCircle2 size={18} className="text-[#22C55E] shrink-0" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-[#D1D5DB] shrink-0 mt-1" />
                          )}
                        </div>
                        <div className="text-[11px] text-[#4B5563] leading-relaxed">{d.sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </section>
      )}

      <div className="pt-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-interest"
          className="btn-shine w-full md:w-auto bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 rounded-md transition-colors inline-flex items-center gap-2"
        >
          {submitting ? (isRegister || isPaid ? "Saving…" : "Submitting…") : (
            isPaid ? "Complete Registration → Join WhatsApp" :
            isRegister ? "Submit & Continue to Payment" :
            "Reserve My Spot"
          )}
          {(isRegister || isPaid) && !submitting && <ArrowRight size={16} />}
        </button>
        <p className="mt-4 text-xs text-[#6B7280]">
          {isPaid
            ? "Your info is saved on our end. After you submit we'll bounce you straight to the SSU Workshops WhatsApp group."
            : isRegister
            ? "Your info is saved on our side the instant you submit. Payment happens on Stripe — receipt comes from them, confirmation from us."
            : "No spam. No pressure. You'll hear from us when tickets open and as event details are confirmed. That's it."}
        </p>
      </div>
    </form>
  );
}
